import { useState, useMemo } from 'react'
import Layout from '../components/Layout'
import { BracketMatchCard, BracketPickCell, BracketActualWinnerCell } from '../components/bracket'

// ─── Layout Constants (mirrored from BracketPicks.jsx) ──────────────────────

const CARD_WIDTH = 280
const ROUND_WIDTH = 320
const ROUND_GAP = 32
const MATCH_SPACING = 40
const HEADER_HEIGHT = 40
const EXTRA_HEIGHT_PADDING = 100
const SVG_DIVIDER_BASE = 58.5

function getLayoutConstants(teamSize) {
  const CELL_H = 40 + Math.max(0, teamSize - 1) * 24
  const MAX_CELLS = 3
  const MATCH_HEIGHT = 38 + MAX_CELLS * CELL_H
  const baseStep = MATCH_HEIGHT + MATCH_SPACING
  return { CELL_H, MATCH_HEIGHT, baseStep }
}

// ─── Mock Player Names ──────────────────────────────────────────────────────

const DOUBLES_NAMES = [
  ['Anna Bright', 'Anna Leigh Waters'],
  ['Rachel Rohrabacher', 'Christian Alshon'],
  ['Valerie Simon', 'Victoria Simon'],
  ['Jade Kawamoto', 'Vivienne David'],
  ['Catherine Parenteau', 'Lea Jansen'],
  ['Jorja Johnson', 'Dylan Frazier'],
  ['Irina Tereschenko', 'Lucy Kovalova'],
  ['Callie Smith', 'Jackie Kawamoto'],
  ['Jessie Irvine', 'Anna Bright'],
  ['Simone Jardim', 'Riley Newman'],
  ['Parris Todd', 'Athena Trouillot'],
  ['Salome Devidze', 'Andrea Koop'],
  ['Meghan Sheehan', 'Etta Wright'],
  ['Lauren Stratman', 'Corrine Carr'],
  ['Mary Brascia', 'Maggie Brascia'],
  ['Brooke Buckner', 'Allyce Jones'],
  ['Lacy Schneemann', 'Megan Fudge'],
  ['Susannah Barr', 'Yana Grechkina'],
  ['Kaitlyn Christian', 'Tina Pisnik'],
  ['Martina Frantova', 'Dominika Mazacova'],
  ['Olga Babiy', 'Tatiana Babiy'],
  ['Mia Babiy', 'Natalia Babiy'],
  ['Elise Jones', 'Sarah Ansboury'],
  ['Tyra Black', 'Paris Crenshaw'],
  ['Lina Padegimaite', 'Erika Andreeva'],
  ['Genie Erokhina', 'Dasha Kovalova'],
  ['Collin Johns', 'Ben Johns'],
  ['Matt Wright', 'Riley Newman'],
  ['JW Johnson', 'Dylan Frazier'],
  ['Tyler Loong', 'Thomas Wilson'],
  ['Jay Devilliers', 'Andrei Daescu'],
  ['Pablo Tellez', 'Federico Staksrud'],
]

const SINGLES_NAMES = [
  ['Ben Johns'], ['Tyson McGuffin'], ['JW Johnson'], ['Federico Staksrud'],
  ['Collin Johns'], ['Riley Newman'], ['Dylan Frazier'], ['Matt Wright'],
  ['Jay Devilliers'], ['Andrei Daescu'], ['Tyler Loong'], ['Thomas Wilson'],
  ['Pablo Tellez'], ['Connor Garnett'], ['Hunter Johnson'], ['Hayden Patriquin'],
  ['Anna Leigh Waters'], ['Anna Bright'], ['Catherine Parenteau'], ['Lea Jansen'],
  ['Jorja Johnson'], ['Parris Todd'], ['Irina Tereschenko'], ['Lucy Kovalova'],
  ['Jessie Irvine'], ['Callie Smith'], ['Simone Jardim'], ['Jackie Kawamoto'],
  ['Salome Devidze'], ['Andrea Koop'], ['Meghan Sheehan'], ['Etta Wright'],
]

// ─── Mock Data Generation ───────────────────────────────────────────────────

function generateTeams(teamSize) {
  const names = teamSize > 1 ? DOUBLES_NAMES : SINGLES_NAMES
  return Array.from({ length: 32 }, (_, i) => ({
    id: `team-${i + 1}`,
    seed: i + 1,
    players: (names[i] || names[i % names.length]).map(name => ({ name })),
  }))
}

function generateMatches() {
  const matches = []
  const rounds = [16, 8, 4, 2, 1]
  rounds.forEach((count, roundIdx) => {
    const roundNum = roundIdx + 1
    for (let m = 1; m <= count; m++) {
      const nextRound = roundNum < 5 ? roundNum + 1 : null
      matches.push({
        match_id: `r${roundNum}-m${m}`,
        round: roundNum,
        match_number: m,
        team_one_id: null,
        team_two_id: null,
        feeds_into_round: nextRound,
        feeds_into_match_number: nextRound ? Math.ceil(m / 2) : null,
        feeds_into_slot: nextRound ? (m % 2 === 1 ? 1 : 2) : null,
      })
    }
  })
  return matches
}

function seedBracket(matches, teams) {
  const r1 = matches.filter(m => m.round === 1).sort((a, b) => a.match_number - b.match_number)
  r1.forEach((match, i) => {
    match.team_one_id = teams[i * 2].id
    match.team_two_id = teams[i * 2 + 1].id
  })
}

function advanceWinner(matches, matchId, winnerId, teams) {
  const match = matches.find(m => m.match_id === matchId)
  if (!match || !match.feeds_into_round) return
  const nextMatch = matches.find(
    m => m.round === match.feeds_into_round && m.match_number === match.feeds_into_match_number
  )
  if (!nextMatch) return
  if (match.feeds_into_slot === 1) {
    nextMatch.team_one_id = winnerId
  } else {
    nextMatch.team_two_id = winnerId
  }
}

function applyScenario(scenario, matches, teams) {
  // userPicks: what the user predicted (always higher seed wins)
  // actualPicks: what actually happened (may differ based on scenario)
  // The user always picks higher seed. We build the user's full predicted bracket first,
  // then build the actual results separately. This lets us show eliminated teams in later rounds.
  const picks = {}
  const teamsById = Object.fromEntries(teams.map(t => [t.id, t]))
  let championTeamId = null

  seedBracket(matches, teams)

  const higherSeedWins = (match) => {
    const t1 = teamsById[match.team_one_id]
    const t2 = teamsById[match.team_two_id]
    if (!t1 || !t2) return match.team_one_id
    return t1.seed < t2.seed ? t1.id : t2.id
  }

  const lowerSeedWins = (match) => {
    const t1 = teamsById[match.team_one_id]
    const t2 = teamsById[match.team_two_id]
    if (!t1 || !t2) return match.team_two_id
    return t1.seed > t2.seed ? t1.id : t2.id
  }

  // For pre-selection, skip all user picks — bracket is empty
  if (scenario === 'pre-selection') {
    matches.forEach(match => {
      picks[match.match_id] = {
        teamId: null,
        winner_team_id: null,
        round: match.round,
        match_number: match.match_number,
      }
    })
    return { picks, championTeamId: null }
  }

  // Build user's predicted bracket (always higher seed wins) on a copy of matches
  const userMatches = matches.map(m => ({ ...m }))
  seedBracket(userMatches, teams)
  const userPicksByMatch = {}
  for (let round = 1; round <= 5; round++) {
    const roundMatches = userMatches.filter(m => m.round === round).sort((a, b) => a.match_number - b.match_number)
    roundMatches.forEach(match => {
      if (!match.team_one_id || !match.team_two_id) return
      const predictedWinner = higherSeedWins(match)
      userPicksByMatch[match.match_id] = predictedWinner
      advanceWinner(userMatches, match.match_id, predictedWinner, teams)
    })
  }

  // Helper: determine actual winner for a match given scenario rules
  const getActualWinner = (match, round, idx, scenarioId) => {
    switch (scenarioId) {
      case 'pre-tournament':
        return null
      case 'perfect-bracket':
        return higherSeedWins(match)
      case 'all-wrong':
        return lowerSeedWins(match)
      case 'mixed-results':
        return (idx + round) % 3 === 0 ? lowerSeedWins(match) : higherSeedWins(match)
      case 'champion-wrong':
        return (round === 5 || idx % 5 === 3) ? lowerSeedWins(match) : higherSeedWins(match)
      case 'tournament-over':
        return (idx % 4 === 2 && round <= 2) ? lowerSeedWins(match) : higherSeedWins(match)
      default:
        return null
    }
  }

  // For timeline scenarios, determine how many rounds have results
  const resolvedRounds = {
    'pre-tournament': 0,
    'round-1-complete': 1,
    'round-2-complete': 2,
    'semis-complete': 4,
    'tournament-over': 5,
    'perfect-bracket': 5,
    'all-wrong': 5,
    'mixed-results': 5,
    'champion-wrong': 5,
  }
  const maxResolvedRound = resolvedRounds[scenario] ?? 0

  // Build actual results on the real matches array
  for (let round = 1; round <= 5; round++) {
    const roundMatches = matches.filter(m => m.round === round).sort((a, b) => a.match_number - b.match_number)
    roundMatches.forEach((match, idx) => {
      if (!match.team_one_id || !match.team_two_id) return

      const userPick = userPicksByMatch[match.match_id]

      if (round <= maxResolvedRound) {
        // For timeline scenarios that use "higher seed wins" for resolved rounds
        let actualWinner
        if (['round-1-complete', 'round-2-complete', 'semis-complete'].includes(scenario)) {
          actualWinner = higherSeedWins(match)
        } else {
          actualWinner = getActualWinner(match, round, idx, scenario)
        }
        picks[match.match_id] = {
          teamId: userPick,
          winner_team_id: actualWinner,
          round: match.round,
          match_number: match.match_number,
        }
        if (actualWinner) {
          advanceWinner(matches, match.match_id, actualWinner, teams)
        }
      } else {
        // Unresolved: advance user's pick for bracket structure
        picks[match.match_id] = {
          teamId: userPick,
          winner_team_id: null,
          round: match.round,
          match_number: match.match_number,
        }
        advanceWinner(matches, match.match_id, userPick, teams)
      }
    })
  }

  championTeamId = userPicksByMatch[matches.find(m => m.round === 5)?.match_id] || teams[0].id

  return { picks, championTeamId }
}

// ─── Bracket Structure Builder ──────────────────────────────────────────────

function buildBracketStructure(matches) {
  const roundsMap = {}
  matches.forEach(m => {
    if (!roundsMap[m.round]) roundsMap[m.round] = []
    roundsMap[m.round].push(m)
  })
  Object.values(roundsMap).forEach(arr => arr.sort((a, b) => a.match_number - b.match_number))
  return Object.keys(roundsMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map(roundNum => ({ roundNum, matches: roundsMap[roundNum] }))
}

// ─── Round Label Helper ─────────────────────────────────────────────────────

function getRoundLabel(roundNum, matchCount) {
  if (matchCount === 1) return 'Championship'
  if (matchCount === 2) return 'Semifinals'
  if (matchCount === 4) return 'Quarterfinals'
  return `Round ${roundNum}`
}

function getPointsForRound(roundNum) {
  const pts = { 1: 1, 2: 2, 3: 4, 4: 8, 5: 16 }
  return pts[roundNum] || 1
}

// ─── Elimination Helpers (mirrored from BracketPicks.jsx) ───────────────────

function isTeamEliminatedInPreviousRounds(teamId, currentMatch, bracketStructure, picks) {
  if (!teamId || !currentMatch || currentMatch.round <= 1) return false

  const prevRoundNum = currentMatch.round - 1
  const prevRoundData = bracketStructure.find(r => r.roundNum === prevRoundNum)
  if (!prevRoundData) return false

  const match1Index = (currentMatch.match_number * 2) - 2
  const match2Index = (currentMatch.match_number * 2) - 1

  const prevMatches = [
    prevRoundData.matches[match1Index],
    prevRoundData.matches[match2Index]
  ].filter(Boolean)

  for (const prevMatch of prevMatches) {
    const prevPick = picks[prevMatch.match_id]
    if (prevPick?.teamId === teamId) {
      if (prevPick.winner_team_id && prevPick.winner_team_id !== teamId) {
        return true
      }
      if (isTeamEliminatedInPreviousRounds(teamId, prevMatch, bracketStructure, picks)) {
        return true
      }
    }
  }

  return false
}

function getPreviousRoundPickForSlot(match, slotIndex, bracketStructure, picks) {
  if (match.round <= 1) return { userPickedTeamId: null, fromIncorrectPick: false }

  const prevRoundData = bracketStructure.find(r => r.roundNum === match.round - 1)
  if (!prevRoundData) return { userPickedTeamId: null, fromIncorrectPick: false }

  const matchIndex = (match.match_number * 2) - 2 + slotIndex
  const prevMatch = prevRoundData.matches[matchIndex]
  if (!prevMatch) return { userPickedTeamId: null, fromIncorrectPick: false }

  const prevPick = picks[prevMatch.match_id]
  if (!prevPick?.teamId) return { userPickedTeamId: null, fromIncorrectPick: false }

  const fromIncorrectPick = !!(prevPick.winner_team_id && prevPick.teamId !== prevPick.winner_team_id)
  return { userPickedTeamId: prevPick.teamId, fromIncorrectPick }
}

// ─── SVG Connector Lines ────────────────────────────────────────────────────

function ConnectorLines({ bracketStructure, teamSize, viewMode }) {
  if (viewMode === 'compact') return null

  const { CELL_H, MATCH_HEIGHT, baseStep } = getLayoutConstants(teamSize)
  const matchPositions = new Map()

  bracketStructure.forEach((round, roundIndex) => {
    const { roundNum, matches: roundMatches } = round
    const roundStep = baseStep * Math.pow(2, roundNum - 1)
    const roundTopOffset = (baseStep * (Math.pow(2, roundNum - 1) - 1)) / 2
    const xPosition = roundIndex * (ROUND_WIDTH + ROUND_GAP)

    roundMatches.forEach(match => {
      const topPosition = roundTopOffset + (match.match_number - 1) * roundStep
      matchPositions.set(match.match_id, {
        x: xPosition,
        y: topPosition + HEADER_HEIGHT * 2,
        width: CARD_WIDTH,
        height: MATCH_HEIGHT,
      })
    })
  })

  const lines = []
  const allMatches = bracketStructure.flatMap(r => r.matches)

  allMatches.forEach(match => {
    const fromPos = matchPositions.get(match.match_id)
    if (!fromPos) return
    const nextRound = match.round + 1
    const nextMatchNumber = Math.ceil(match.match_number / 2)
    const targetMatch = allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber)
    if (!targetMatch) return
    const toPos = matchPositions.get(targetMatch.match_id)
    if (!toPos) return

    const fromX = fromPos.x + fromPos.width / 2
    const fromY = fromPos.y + SVG_DIVIDER_BASE + CELL_H
    const toX = toPos.x + toPos.width / 2
    const toY = toPos.y + SVG_DIVIDER_BASE + CELL_H
    const gapMidX = fromPos.x + fromPos.width + (toPos.x - (fromPos.x + fromPos.width)) / 2
    const path = `M ${fromX} ${fromY} L ${gapMidX} ${fromY} L ${gapMidX} ${toY} L ${toX} ${toY}`

    lines.push({ id: `${match.match_id}-to-${targetMatch.match_id}`, path })
  })

  const totalWidth = bracketStructure.length * (ROUND_WIDTH + ROUND_GAP)
  let maxHeight = 0
  matchPositions.forEach(pos => {
    const bottom = pos.y + pos.height
    if (bottom > maxHeight) maxHeight = bottom
  })

  return (
    <svg
      className="absolute top-0 pointer-events-none"
      width={totalWidth}
      height={maxHeight + 100}
      style={{ zIndex: 0 }}
    >
      {lines.map(line => (
        <path key={line.id} d={line.path} fill="none" stroke="var(--mauve-7)" strokeWidth="1" />
      ))}
    </svg>
  )
}


// ─── Scenario Definitions ───────────────────────────────────────────────────

const SCENARIOS = [
  { id: 'pre-selection', label: 'Pre-Selection', group: 'Timeline' },
  { id: 'pre-tournament', label: 'Pre-Tournament', group: 'Timeline' },
  { id: 'round-1-complete', label: 'Round 1 Complete', group: 'Timeline' },
  { id: 'round-2-complete', label: 'Round 2 Complete', group: 'Timeline' },
  { id: 'semis-complete', label: 'Semis Complete', group: 'Timeline' },
  { id: 'tournament-over', label: 'Tournament Over', group: 'Timeline' },
  { id: 'perfect-bracket', label: 'Perfect Bracket', group: 'Outcomes' },
  { id: 'all-wrong', label: 'All Wrong', group: 'Outcomes' },
  { id: 'mixed-results', label: 'Mixed Results', group: 'Outcomes' },
  { id: 'champion-wrong', label: 'Champion Wrong', group: 'Outcomes' },
]

// ─── Main Page Component ────────────────────────────────────────────────────

export default function DevBracket() {
  const [scenario, setScenario] = useState('tournament-over')
  const [teamSize, setTeamSize] = useState(2)
  const [viewMode, setViewMode] = useState('staggered')

  const { teams, matches, bracketStructure, picks, championTeamId, championWon } = useMemo(() => {
    const t = generateTeams(teamSize)
    const m = generateMatches()
    const { picks: p, championTeamId: cid } = applyScenario(scenario, m, t)
    const bs = buildBracketStructure(m)
    const champMatch = m.find(match => match.round === 5)
    const champPick = champMatch ? p[champMatch.match_id] : null
    const won = !!(cid && champPick?.winner_team_id && champPick.winner_team_id === cid)
    return { teams: t, matches: m, bracketStructure: bs, picks: p, championTeamId: cid, championWon: won }
  }, [scenario, teamSize])

  const teamsById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams])

  const { CELL_H, MATCH_HEIGHT, baseStep } = getLayoutConstants(teamSize)
  const isCompact = viewMode === 'compact'

  return (
    <Layout>
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Bracket Demo</h1>
          <p className="text-muted-foreground mt-1">Interactive bracket visualization with scenario-driven card states.</p>
        </div>

        {/* Control Bar */}
        <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-[var(--color-bracket-card-border)] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Timeline Selector */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Timeline</label>
              <div className="flex border border-[var(--color-bracket-card-border)] rounded-md overflow-hidden">
                {SCENARIOS.filter(s => s.group === 'Timeline').map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setScenario(s.id)}
                    className={`text-sm px-3 py-1.5 transition-colors ${i > 0 ? 'border-l border-[var(--color-bracket-card-border)]' : ''} ${scenario === s.id ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Outcome Selector */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Outcome</label>
              <div className="flex border border-[var(--color-bracket-card-border)] rounded-md overflow-hidden">
                {SCENARIOS.filter(s => s.group === 'Outcomes').map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setScenario(s.id)}
                    className={`text-sm px-3 py-1.5 transition-colors ${i > 0 ? 'border-l border-[var(--color-bracket-card-border)]' : ''} ${scenario === s.id ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Format Toggle */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Format</label>
              <div className="flex border border-[var(--color-bracket-card-border)] rounded-md overflow-hidden">
                <button
                  onClick={() => setTeamSize(2)}
                  className={`text-sm px-3 py-1.5 transition-colors ${teamSize === 2 ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                >
                  Doubles
                </button>
                <button
                  onClick={() => setTeamSize(1)}
                  className={`text-sm px-3 py-1.5 transition-colors border-l border-[var(--color-bracket-card-border)] ${teamSize === 1 ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                >
                  Singles
                </button>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">View</label>
              <div className="flex border border-[var(--color-bracket-card-border)] rounded-md overflow-hidden">
                <button
                  onClick={() => setViewMode('staggered')}
                  className={`text-sm px-3 py-1.5 transition-colors ${viewMode === 'staggered' ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                >
                  Tree
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  className={`text-sm px-3 py-1.5 transition-colors border-l border-[var(--color-bracket-card-border)] ${viewMode === 'compact' ? 'bg-[var(--purple-3)] text-[var(--purple-11)] font-semibold' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                >
                  Compact
                </button>
              </div>
            </div>

            {/* Scenario Description */}
            <div className="ml-auto text-xs text-muted-foreground max-w-xs text-right hidden sm:block">
              {scenario === 'pre-selection' && 'No picks made yet. Teams seeded, all cells selectable.'}
              {scenario === 'pre-tournament' && 'All picks made, no results yet. Pending points shown.'}
              {scenario === 'round-1-complete' && 'Round 1 resolved with mix of correct/wrong. R2+ pending.'}
              {scenario === 'round-2-complete' && 'Rounds 1-2 resolved. R3+ still pending.'}
              {scenario === 'semis-complete' && 'Through semifinals. Championship pending.'}
              {scenario === 'tournament-over' && 'All matches resolved. Mix of correct/wrong, champion crowned.'}
              {scenario === 'perfect-bracket' && 'Every pick correct. Champion correct with neon treatment.'}
              {scenario === 'all-wrong' && 'Every pick wrong. Actual winners shown with CheckCircle.'}
              {scenario === 'mixed-results' && 'Realistic mix of ~60% correct. Champion correct.'}
              {scenario === 'champion-wrong' && 'Mixed results but championship pick is wrong.'}
            </div>
          </div>
        </div>

        {/* Bracket Visualization */}
        <div className="overflow-x-scroll scrollbar-visible pl-1 py-2">
          <div className="relative min-w-max" style={{ display: 'flex', justifyContent: 'center' }}>
            {/* SVG Connector Lines */}
            <ConnectorLines
              bracketStructure={bracketStructure}
              teamSize={teamSize}
              viewMode={viewMode}
            />

            {/* Round Columns */}
            <div className="flex relative z-[1]" style={{ gap: `${ROUND_GAP}px` }}>
              {bracketStructure.map(({ roundNum, matches: roundMatches }, roundIndex) => {
                const roundLabel = getRoundLabel(roundNum, roundMatches.length)
                const maxMatchNumber = Math.max(...roundMatches.map(m => m.match_number))
                const roundStep = isCompact ? baseStep : baseStep * Math.pow(2, roundNum - 1)
                const roundTopOffset = isCompact ? 0 : (baseStep * (Math.pow(2, roundNum - 1) - 1)) / 2
                const totalHeight = isCompact
                  ? (maxMatchNumber - 1) * baseStep + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING
                  : HEADER_HEIGHT + roundTopOffset + (maxMatchNumber - 1) * roundStep + MATCH_HEIGHT + EXTRA_HEIGHT_PADDING

                return (
                  <div key={roundNum} className={isCompact ? '' : 'relative'} style={{ width: `${ROUND_WIDTH}px`, minHeight: isCompact ? 'auto' : `${totalHeight}px` }}>
                    {/* Round Header */}
                    <div className="flex items-center justify-between mb-4 z-10 bg-transparent py-2" style={{ height: `${HEADER_HEIGHT}px` }}>
                      <h3 className="text-left font-bold text-sm text-muted-foreground">{roundLabel}</h3>
                    </div>

                    {/* Matches */}
                    <div className={isCompact ? 'flex flex-col gap-6' : 'relative'} style={isCompact ? {} : { minHeight: `${totalHeight - 50}px` }}>
                      {roundMatches.map(match => {
                        const topPosition = isCompact
                          ? 0
                          : HEADER_HEIGHT + roundTopOffset + (match.match_number - 1) * roundStep

                        const pick = picks[match.match_id]

                        // For rounds > 1, get the user's predicted team from the previous round
                        // (which may differ from the actual winner that advanced)
                        let userPickedTeam1Id = match.team_one_id
                        let userPickedTeam2Id = match.team_two_id
                        let team1FromIncorrectPick = false
                        let team2FromIncorrectPick = false

                        if (match.round > 1) {
                          const slot1 = getPreviousRoundPickForSlot(match, 0, bracketStructure, picks)
                          const slot2 = getPreviousRoundPickForSlot(match, 1, bracketStructure, picks)
                          if (slot1.userPickedTeamId) {
                            userPickedTeam1Id = slot1.userPickedTeamId
                            team1FromIncorrectPick = slot1.fromIncorrectPick
                          }
                          if (slot2.userPickedTeamId) {
                            userPickedTeam2Id = slot2.userPickedTeamId
                            team2FromIncorrectPick = slot2.fromIncorrectPick
                          }
                        }

                        const team1 = userPickedTeam1Id ? teamsById[userPickedTeam1Id] : null
                        const team2 = userPickedTeam2Id ? teamsById[userPickedTeam2Id] : null

                        const team1EliminatedEarlier = userPickedTeam1Id
                          ? isTeamEliminatedInPreviousRounds(userPickedTeam1Id, match, bracketStructure, picks)
                          : false
                        const team2EliminatedEarlier = userPickedTeam2Id
                          ? isTeamEliminatedInPreviousRounds(userPickedTeam2Id, match, bracketStructure, picks)
                          : false

                        const currentPick = picks[match.match_id]
                        const isTeam1Selected = currentPick?.teamId === userPickedTeam1Id && userPickedTeam1Id != null
                        const isTeam2Selected = currentPick?.teamId === userPickedTeam2Id && userPickedTeam2Id != null

                        const hasOfficialWinner = !!currentPick?.winner_team_id
                        const isPickIncorrect = hasOfficialWinner && currentPick?.teamId && currentPick?.teamId !== currentPick?.winner_team_id
                        const isPickCorrect = hasOfficialWinner && currentPick?.teamId && currentPick?.teamId === currentPick?.winner_team_id

                        const selectedTeamEliminated = (isTeam1Selected && (isPickIncorrect || team1FromIncorrectPick || team1EliminatedEarlier)) ||
                                                       (isTeam2Selected && (isPickIncorrect || team2FromIncorrectPick || team2EliminatedEarlier))

                        const actualWinnerId = currentPick?.winner_team_id
                        const showActualWinner = isPickIncorrect && actualWinnerId
                          && actualWinnerId !== userPickedTeam1Id && actualWinnerId !== userPickedTeam2Id

                        const isTeam1Champion = championTeamId === userPickedTeam1Id
                        const isTeam2Champion = championTeamId === userPickedTeam2Id
                        const isChampionMatch = isTeam1Champion || isTeam2Champion

                        const isTeam1Winner = isTeam1Selected && isPickCorrect
                        const isTeam2Winner = isTeam2Selected && isPickCorrect

                        const cellCount = 2 + (showActualWinner ? 1 : 0)
                        const cardMinHeight = 38 + cellCount * CELL_H

                        return (
                          <BracketMatchCard
                            key={match.match_id}
                            matchId={match.match_id}
                            roundNum={roundNum}
                            matchNumber={match.match_number}
                            isPickCorrect={isPickCorrect}
                            isPickIncorrect={isPickIncorrect}
                            isChampionMatch={isChampionMatch}
                            selectedTeamEliminated={selectedTeamEliminated}
                            hasOfficialWinner={hasOfficialWinner}
                            hasPick={!!currentPick?.teamId}
                            points={getPointsForRound(roundNum)}
                            cardMinHeight={cardMinHeight}
                            topPosition={topPosition}
                            cardWidth={CARD_WIDTH}
                            isCompact={isCompact}
                          >
                            <BracketPickCell
                              team={team1}
                              teamSize={teamSize}
                              teamId={userPickedTeam1Id}
                              isSelected={isTeam1Selected}
                              isPickCorrect={isPickCorrect}
                              isPickIncorrect={isPickIncorrect}
                              fromIncorrectPick={team1FromIncorrectPick}
                              eliminatedEarlier={team1EliminatedEarlier}
                              isChampion={isTeam1Champion}
                              isChampionCorrect={championWon && championTeamId === userPickedTeam1Id}
                              isWinner={isTeam1Winner}
                              hasOfficialWinner={hasOfficialWinner}
                              officialWinnerId={currentPick?.winner_team_id}
                              championTeamId={championTeamId}
                              showBorderB={!showActualWinner}
                              actualWinnerShown={!!showActualWinner}
                            />

                            {showActualWinner && (
                              <BracketActualWinnerCell
                                team={teamsById[actualWinnerId]}
                                teamSize={teamSize}
                                isWinner
                                isChampionCorrect={championWon && championTeamId === actualWinnerId}
                                showBorderB
                              />
                            )}

                            <BracketPickCell
                              team={team2}
                              teamSize={teamSize}
                              teamId={userPickedTeam2Id}
                              isSelected={isTeam2Selected}
                              isPickCorrect={isPickCorrect}
                              isPickIncorrect={isPickIncorrect}
                              fromIncorrectPick={team2FromIncorrectPick}
                              eliminatedEarlier={team2EliminatedEarlier}
                              isChampion={isTeam2Champion}
                              isChampionCorrect={championWon && championTeamId === userPickedTeam2Id}
                              isWinner={isTeam2Winner}
                              hasOfficialWinner={hasOfficialWinner}
                              officialWinnerId={currentPick?.winner_team_id}
                              championTeamId={championTeamId}
                              showBorderB={false}
                              actualWinnerShown={!!showActualWinner}
                            />
                          </BracketMatchCard>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
